/* eslint-disable @atlassian/relay/graphql-naming */
import { graphql, readInlineData } from 'react-relay';
import type { FragmentRefs } from 'relay-runtime';
import type { processFields$key } from './__generated__/processFields.graphql';
import type { processFields414BadPattern$key } from './__generated__/processFields414BadPattern.graphql';
import type { JiraIssueFieldConfig } from './JiraFieldCategoryType.types.tsx';

/*
 * This is to define the data that we want to consume from the JiraIssueFieldConfig
 * We can reuse this fragment in multiple places to make sure we are consuming the same data
 * e.g you can spread this on the return type of mutations to ensure we fetching the required data for our table instead of writing it out again
 * Please do not use this for anything else, e.g if you are querying for the field node in the side bar, write out your own fragment
 */
export const readInlineJiraFieldConfig = (
	queryRef?: processFields$key | null,
): JiraIssueFieldConfig | null => {
	const node = readInlineData(
		graphql`
			fragment processFields on JiraIssueFieldConfig
			@argumentDefinitions(
				includeInstalledByAppName: { type: "Boolean", defaultValue: false }
				includeIsForge: { type: "Boolean", defaultValue: false }
			)
			@inline {
				id
				__typename
				fieldId
				name
				type
				typeKey
				typeDisplayName
				installedByAppName @include(if: $includeInstalledByAppName)
				description
				lastUsedTimestamp
				isLocked
				isCustom
				isGlobal
				isTrashed
				isUnscreenable
				customId
				associatedProjectsCount
				associatedScreensCount
				associatedContextsCount
				trashedTimestamp
				plannedDeletionTimestamp
				adminPagesFieldsIsHighlighted
				isForge @include(if: $includeIsForge)
				isConnect @include(if: $includeIsForge)
				trashedByUser {
					__typename
					name
					accountId
				}
			}
		`,
		queryRef,
	);

	if (!node) return null;

	return {
		' $fragmentType': 'processFields',
		id: node.id,
		__typename: node.__typename ?? 'JiraIssueFieldConfig',
		fieldId: node.fieldId,
		name: node.name,
		type: node.type,
		typeKey: node.typeKey,
		typeDisplayName: node.typeDisplayName,
		installedByAppName: node.installedByAppName,
		description: node?.description,
		lastUsedTimestamp: node?.lastUsedTimestamp,
		isLocked: node.isLocked,
		isCustom: node.isCustom,
		isGlobal: node.isGlobal,
		isUnscreenable: node.isUnscreenable,
		customId: node.customId,
		isTrashed: node.isTrashed,
		associatedContextsCount: node?.associatedContextsCount,
		associatedScreensCount: node?.associatedScreensCount,
		associatedProjectsCount: node?.associatedProjectsCount,
		trashedTimestamp: node?.trashedTimestamp,
		plannedDeletionTimestamp: node?.plannedDeletionTimestamp,
		adminPagesFieldsIsHighlighted: node?.adminPagesFieldsIsHighlighted,
		isForge: node.isForge,
		isConnect: node.isConnect,
		trashedByUser: node?.trashedByUser
			? {
					__typename: node.trashedByUser.__typename ?? 'User',
					name: node.trashedByUser.name ?? '',
					accountId: node.trashedByUser.accountId ?? '',
				}
			: null,
	};
};

/**
 * This is to consume the data , because shoving into charlie table will produce random lint errors, since it doesn't know its using it
 * @param queryRef - The query ref on JiraIssueFieldConfig
 * @returns The field
 */
const processOneField = (queryRef: processFields414BadPattern$key): JiraIssueFieldConfig | null => {
	/**
	 * Warning: Do not rename this query, else you will be in a world of pain
	 */
	const queryData = readInlineData(
		graphql`
			fragment processFields414BadPattern on JiraFieldConfigEdge
			@argumentDefinitions(
				includeIsForge: { type: "Boolean", defaultValue: false }
				includeInstalledByAppName: { type: "Boolean", defaultValue: false }
			)
			@inline {
				node {
					...processFields
						@arguments(
							includeIsForge: $includeIsForge
							includeInstalledByAppName: $includeInstalledByAppName
						)
				}
			}
		`,
		queryRef,
	);

	if (!queryData?.node) return null;

	return readInlineJiraFieldConfig(queryData.node);
};

/**
 * Utility function to process a list of JiraFieldConfigEdge's, removes null nodes
 * You shouldn't really be using this, Its only used to generate the data for charlie table
 */
export const processFields = (
	edges?:
		| readonly {
				readonly ' $fragmentSpreads': FragmentRefs<'processFields414BadPattern'>;
		  }[]
		| null,
): JiraIssueFieldConfig[] =>
	edges?.reduce<JiraIssueFieldConfig[]>((acc, node) => {
		const field = processOneField(node);
		if (field) acc.push(field);
		return acc;
	}, []) ?? [];
